import { useState, useRef } from 'react';
import JSZip from 'jszip';

const CONCURRENCY = 6;

/**
 * Downloads a batch's QR art files from Cloudinary and assembles the zip
 * in the browser (server zips would exceed Vercel's response size limit).
 * Validates via /art-manifest first — any flagged order blocks the download.
 */
export default function ArtZipButton({ label, className, children }) {
  const [state, setState] = useState(null); // {phase, done, total, issues, message}
  const cancelRef = useRef(false);

  const reset = () => {
    cancelRef.current = true;
    setState(null);
  };

  const run = async () => {
    cancelRef.current = false;
    setState({ phase: 'manifest' });
    try {
      const res = await fetch(`/api/greenlight/export/${encodeURIComponent(label)}/art-manifest`);
      const manifest = await res.json();
      if (!res.ok) throw new Error(manifest.error || 'Failed to load manifest');

      if (!manifest.ok) {
        setState({ phase: 'issues', issues: manifest.issues });
        return;
      }

      const items = [];
      for (const order of manifest.orders) {
        for (const f of order.files) {
          items.push({ order: order.order_uid, filename: f.filename, url: f.url });
        }
      }

      const total = items.length;
      let done = 0;
      setState({ phase: 'downloading', done, total });

      // Fetch with limited concurrency; store blobs, then add to the zip in
      // manifest order so folder ordering stays deterministic.
      const blobs = new Map();
      const queue = [...items];
      const fetchOne = async (item, attempt = 1) => {
        try {
          const r = await fetch(item.url);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          blobs.set(`${item.order}/${item.filename}`, await r.blob());
        } catch (err) {
          if (attempt < 3) return fetchOne(item, attempt + 1);
          throw new Error(`Failed to download ${item.order}/${item.filename}: ${err.message}`);
        }
      };
      const workers = Array.from({ length: CONCURRENCY }, async () => {
        while (queue.length > 0) {
          if (cancelRef.current) return;
          const item = queue.shift();
          await fetchOne(item);
          done++;
          setState({ phase: 'downloading', done, total });
        }
      });
      await Promise.all(workers);
      if (cancelRef.current) return;

      setState({ phase: 'zipping', done: total, total });
      const zip = new JSZip();
      const muse = zip.folder('muse');
      for (const item of items) {
        muse.folder(item.order).file(item.filename, blobs.get(`${item.order}/${item.filename}`));
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      if (cancelRef.current) return;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setState(null);
    } catch (err) {
      if (!cancelRef.current) setState({ phase: 'error', message: err.message });
    }
  };

  return (
    <>
      <button onClick={run} className={className} type="button">
        {children}
      </button>

      {state && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              QR Art Zip — <span className="font-mono">{label}</span>
            </h3>

            {state.phase === 'manifest' && (
              <p className="text-sm text-gray-500">Checking art files…</p>
            )}

            {state.phase === 'issues' && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3">
                  <p className="text-sm font-semibold text-red-800 mb-1.5">
                    Batch is not ready — fix these before downloading:
                  </p>
                  <ul className="text-xs text-red-700 font-mono space-y-0.5 max-h-52 overflow-y-auto">
                    {state.issues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-gray-400">
                  No zip was created. Every MSE order needs 25 codes with art files,
                  including one dealerCheckIn and one dealerServicing stand.
                </p>
              </>
            )}

            {(state.phase === 'downloading' || state.phase === 'zipping') && (
              <>
                <p className="text-sm text-gray-600 mb-2">
                  {state.phase === 'downloading'
                    ? `Downloading art files… ${state.done} of ${state.total}`
                    : 'Building zip…'}
                </p>
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-emerald-500 h-2 transition-all"
                    style={{ width: `${state.total ? Math.round((state.done / state.total) * 100) : 0}%` }}
                  />
                </div>
              </>
            )}

            {state.phase === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {state.message}
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={reset}
                className="px-4 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800"
              >
                {state.phase === 'downloading' || state.phase === 'zipping' ? 'Cancel' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
